
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles, categories } from '../../mocks/articles';

export default function ArticlesPage() {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArticles = articles.filter((article) => {
    const matchesCategory = selectedCategory === 'Tous' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="px-20 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/2b97dc596bbe8fcb59c5903c7da9385e/8fbf45a2724c466b60f81eb478c19778.jpeg" 
                alt="ATMA Consulting" 
                className="h-20 w-auto object-contain"
              />
            </Link>
            
            <div className="flex items-center gap-8">
              <Link to="/management-leadership" className="text-gray-700 hover:text-[#C9A961] transition-colors duration-300 text-base font-medium whitespace-nowrap">
                Management & Leadership
              </Link>
              <Link to="/strategie-operationnelle" className="text-gray-700 hover:text-[#C9A961] transition-colors duration-300 text-base font-medium whitespace-nowrap">
                Stratégie Opérationnelle
              </Link>
              <Link to="/suivi-productivite" className="text-gray-700 hover:text-[#C9A961] transition-colors duration-300 text-base font-medium whitespace-nowrap">
                Suivi & Productivité
              </Link>
              <Link to="/rh-developpement" className="text-gray-700 hover:text-[#C9A961] transition-colors duration-300 text-base font-medium whitespace-nowrap">
                RH & Développement
              </Link>
              <Link to="/articles" className="text-[#C9A961] font-semibold transition-colors duration-300 text-base whitespace-nowrap">
                Articles
              </Link>
            </div>

            <Link to="/#contact" className="bg-[#1A2B4A] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#C9A961] transition-all duration-300 whitespace-nowrap">
              Nous Contacter
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/0bc95c0cc009120403166813dd3b8699.jpg" 
            alt="Articles" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A2B4A]/80 via-[#1A2B4A]/70 to-[#1A2B4A]/80"></div>
        </div>

        <div className="relative z-10 px-20 text-center w-full">
          <div className="text-sm text-white/70 mb-6">
            <Link to="/" className="hover:text-white transition-colors duration-300">Accueil</Link>
            <span className="mx-3">&gt;</span>
            <span>Articles</span>
          </div>
          <h1 className="text-8xl font-bold text-white mb-6">
            Articles
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Découvrez nos réflexions, conseils et analyses sur le management, la stratégie et le développement des organisations
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-12 px-20 bg-[#F5F5F7] border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-300 focus:border-[#C9A961] focus:ring-2 focus:ring-[#C9A961]/20 outline-none transition-all duration-300 text-sm"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap items-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-[#1A2B4A] text-white'
                      : 'bg-white text-gray-700 hover:bg-[#1A2B4A] hover:text-white border border-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 px-20">
        <div className="max-w-7xl mx-auto">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#C9A961] text-white px-4 py-1 rounded-full text-xs font-semibold">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {new Date(article.date).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#1A2B4A] mb-3 group-hover:text-[#C9A961] transition-colors duration-300 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-6">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        Par {article.author}
                      </span>
                      <span className="text-[#C9A961] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                        Lire l'article
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-article-line text-6xl text-gray-300 mb-6"></i>
              <h3 className="text-2xl font-bold text-[#1A2B4A] mb-4">Aucun article trouvé</h3>
              <p className="text-gray-600">Essayez de modifier vos critères de recherche</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 px-20 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Restez Informé
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Écrivez-nous pour en parler
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="mailto:michiele.tornabene@atmahealthcareconsulting.com"
              className="inline-block bg-gradient-to-r from-[#C9A961] to-[#B8935A] text-white px-10 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Écrire un E-mail
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1A2E] text-white py-20 px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-4 gap-12 mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/2b97dc596bbe8fcb59c5903c7da9385e/8fbf45a2724c466b60f81eb478c19778.jpeg" 
                alt="ATMA Consulting" 
                className="h-16 w-auto object-contain mb-6"
              />
              <p className="text-gray-400 mb-6 leading-relaxed">
                Excellence opérationnelle et humaine
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/company/atma-healthcare-consulting-1/posts/?feedView=all" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#C9A961] transition-colors duration-300 cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase text-gray-400 mb-6 font-semibold">Nos Services</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/management-leadership" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    Management & Leadership
                  </Link>
                </li>
                <li>
                  <Link to="/strategie-operationnelle" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    Stratégie Opérationnelle
                  </Link>
                </li>
                <li>
                  <Link to="/suivi-productivite" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    Suivi & Productivité
                  </Link>
                </li>
                <li>
                  <Link to="/rh-developpement" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    RH & Développement
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm uppercase text-gray-400 mb-6 font-semibold">Contact</h4>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:michiele.tornabene@atmahealthcareconsulting.com" className="text-white hover:text-[#C9A961] transition-colors duration-300 text-xs whitespace-nowrap">
                    michiele.tornabene@atmahealthcareconsulting.com
                  </a>
                </li>
                <li className="text-gray-400 leading-relaxed">
                  2570 Route de Loqui<br />13290 Aix-en-Provence
                </li>
              </ul>
            </div>

            <div className="pl-8">
              <h4 className="text-sm uppercase text-gray-400 mb-6 font-semibold">À Propos</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/qui-sommes-nous" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    Qui sommes-nous ?
                  </Link>
                </li>
                <li>
                  <Link to="/nos-valeurs" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    Nos Valeurs
                  </Link>
                </li>
                <li>
                  <Link to="/articles" className="text-white hover:text-[#C9A961] transition-colors duration-300">
                    Articles
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              © 2025 ATMA Consulting. Tous droits réservés.
            </p>
            <div className="flex gap-8">
              <Link to="/mentions-legales" className="text-gray-400 hover:text-[#C9A961] transition-colors duration-300 text-sm">
                Mentions Légales
              </Link>
              <Link to="/confidentialite" className="text-gray-400 hover:text-[#C9A961] transition-colors duration-300 text-sm">
                Confidentialité
              </Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C9A961] transition-colors duration-300 text-sm">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
